import type { AxiosError } from 'axios'

export type AppError = AxiosError | Error

export class ErrorHandler {
    static getUserFriendlyMessage(error: AppError): string {
        if ('response' in error && error.response) {
            const status = error.response.status
            const data = error.response.data as { detail?: string; message?: string; error?: string }

            switch (status) {
                case 400:
                    return data?.detail || data?.message || 'Invalid request'
                case 401:
                    return 'Authentication required'
                case 403:
                    return 'Permission denied'
                case 404:
                    return 'Not found'
                case 500:
                    return 'Server error'
                default:
                    return data?.detail || data?.message || 'An error occurred'
            }
        }

        if (error instanceof Error) {
            return error.message || 'An unexpected error occurred'
        }

        return 'An unexpected error occurred'
    }

    static isRetryable(error: AppError): boolean {
        if ('response' in error && error.response) {
            const status = error.response.status
            return status >= 500 || status === 408 || status === 429
        }

        return false
    }

    static logError(error: AppError, context?: string): void {
        if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.error(context || 'Error:', error)
        }
    }
}

export const useErrorHandling = () => {
    const handleError = (error: AppError, context?: string) => {
        ErrorHandler.logError(error, context)

        const message = ErrorHandler.getUserFriendlyMessage(error)
        const isRetryable = ErrorHandler.isRetryable(error)

        return {
            message,
            isRetryable,
        }
    }

    return { handleError }
}
